import {AppConfig, BaseApp, BaseAppProps, BaseAppState} from "../../../shared/BaseApp";
import React, {ReactElement} from 'react';
import {createTheme, Typography} from "@mui/material";
import {DEFAULT_THEME_OPTIONS} from "../../../shared/colors";
import {BaseAppPrefs} from "../../../shared/BaseAppPrefs";
import {LoginConfig} from "../../../shared/Login";
import defaultUserImage from "./res/default_user.png";
import {StyledBoxRow, StyledSpan} from "../../../shared/StyledComponents";
import {CARD_SHADOW, DIVIDER, PD_LG, PD_MD, PD_SM, PD_XLG, PD_XSM, SZ_SM, SZ_SMSM} from "../../../shared/dimens";
import peppinStamp from "./res/peppin_logotype.svg";
import {ProvisionedApp} from "../../../shared/ProvisionedApp";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../../../shared/BaseFragment";
import {Action} from "../../../shared/types";
import {getMemberAuth} from "../../../shared/auth";
import {FirebaseOptions} from "firebase/app";
import {HeaderToolbar} from "./Components";

export type PeppinMainProps = BaseFragmentProps & {}

export type PeppinMainState = BaseFragmentState & {}

export abstract class PeppinMain<P extends PeppinMainProps = PeppinMainProps, S extends PeppinMainState = PeppinMainState> extends BaseFragment<P, S> {

  protected readonly memberAuth = getMemberAuth();

  protected renderHeaderToolbar(): ReactElement {
    return null;
  }

  protected createToolbarActions(): Action[] {
    return null;
  }

  private _headerToolbar: ReactElement;

  renderContainerContent(): ReactElement {
    if (this._headerToolbar === undefined) {
      this._headerToolbar = this.renderHeaderToolbar();
      if (!this._headerToolbar) {
        const actions = this.createToolbarActions();
        if (actions?.length > 0) {
          this._headerToolbar = <HeaderToolbar path={this.props.path} actions={actions}/>
        }
      }
    }
    const headerHeight = 72;
    return <>
      <StyledBoxRow
        style={{
          position: "absolute",
          paddingRight: PD_MD,
          gap: PD_LG,
          width: "100%",
          zIndex: 1,
          boxSizing: "border-box",
          height: headerHeight,
          alignItems: "center",
          borderBottom: DIVIDER
        }}>
        <img src={BaseApp.CONTEXT.getAppConfig().icon} style={{width: 72, height: 72, objectFit: "cover"}}/>
        <Typography variant="h5">{BaseApp.CONTEXT.getAppConfig().name}</Typography>
        <StyledSpan/>
        {this._headerToolbar}
      </StyledBoxRow>
      <div style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        paddingTop: headerHeight,
        overflow: "hidden"
      }}>
        {this.renderContent()}
      </div>
    </>;
  }

  abstract renderContent(): ReactElement;
}

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

const THEME = createTheme({
  ...DEFAULT_THEME_OPTIONS,
  components: {
    ...DEFAULT_THEME_OPTIONS.components,
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          whiteSpace: "nowrap",
          boxShadow: CARD_SHADOW,
          paddingTop: PD_XSM,
          paddingBottom: PD_XSM,
          paddingLeft: PD_XLG,
          paddingRight: PD_XLG,
          textTransform: "none",
          borderRadius: SZ_SM,
          // fontWeight: "bold",
        },
      }
    },
  },
  typography: {
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    fontFamily: "Poppins, sans-serif",
    fontSize: 14,
  },
});

class AppPrefs extends BaseAppPrefs {

  private static instance: AppPrefs;

  static getInstance() {
    if (!this.instance) {
      this.instance = new AppPrefs();
    }
    return this.instance;
  }
}

export abstract class PeppinApp extends ProvisionedApp<AppProps, AppState> {

  getAppPrefs<T extends BaseAppPrefs>(): T {
    return AppPrefs.getInstance() as T;
  }

  protected abstract getAppName(): string;

  protected getLoginConfig(): LoginConfig | null | undefined {
    return {
      optionalLogin: true,
      provisioning: {
        usernameEnabled: true,
      },
      uiProps: {
        bottomDecorator: this.renderLoginDecorator(),
      },
    };
  }

  protected abstract getFirebaseOptions(): FirebaseOptions;

  private renderLoginDecorator(): ReactElement {
    return <StyledBoxRow style={{padding: PD_SM}}>
      <StyledSpan/>
      <img src={peppinStamp} style={{height: SZ_SMSM, marginBottom: -SZ_SMSM, opacity: 0.33}}/>
      <StyledSpan/>
    </StyledBoxRow>;
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: this.getAppName(),
      icon: "/icon.png",
      logo: "/logo192.png",
      stamp: "/stamp.svg",
      stampText: this.getAppName(),
      defaultUserImage: defaultUserImage,
      theme: THEME,
      privacyUrl: "",
      termsUrl: "",
      loginConfig: this.getLoginConfig(),
      firebaseConfig: {options: this.getFirebaseOptions()},
    };
  }
}